import {
  FinancialPostingRuleRecord,
  JournalSourceModule,
} from '../../../../types/accounting';
import {
  createPostingRuleFormFromEventDefinition,
  PostingRuleFormState,
} from './postingRules';

export interface RefundPostingRuleTemplate {
  id: string;
  title: string;
  description: string;
  sourceModule: JournalSourceModule;
  eventType: string;
  ruleNamePrefix: string;
}

export const REFUND_POSTING_RULE_TEMPLATES: RefundPostingRuleTemplate[] = [
  {
    id: 'sales-order-refund',
    title: 'Sales order refund',
    description:
      'Reverse revenue, tax, and settlement impact for refunded outlet sales.',
    sourceModule: 'sales',
    eventType: 'sales_order_refund',
    ruleNamePrefix: 'Sales refund',
  },
  {
    id: 'accommodation-booking-refund',
    title: 'Cancelled paid booking',
    description:
      'Refund paid accommodation bookings by unwinding unearned revenue and settlement balances.',
    sourceModule: 'accommodation',
    eventType: 'accommodation_booking_refund',
    ruleNamePrefix: 'Booking refund',
  },
  {
    id: 'accommodation-cancellation-fee',
    title: 'Cancellation fee retained',
    description:
      'Split cancelled booking reversals between customer refund and retained cancellation fee revenue.',
    sourceModule: 'accommodation',
    eventType: 'accommodation_cancellation_fee_retained',
    ruleNamePrefix: 'Cancellation fee',
  },
  {
    id: 'booking-com-refund',
    title: 'Booking.com payout adjustment',
    description:
      'Handle OTA refund and payout correction entries when Booking.com adjusts collectible balances.',
    sourceModule: 'booking_com',
    eventType: 'booking_com_refund_adjustment',
    ruleNamePrefix: 'Booking.com refund',
  },
];

const REFUND_EVENT_TYPES = new Set(
  REFUND_POSTING_RULE_TEMPLATES.map((template) => template.eventType),
);

export const isRefundPostingRule = (
  rule: FinancialPostingRuleRecord,
): boolean => REFUND_EVENT_TYPES.has(rule.event_type);

export const createPostingRuleFormFromRefundTemplate = (
  template: RefundPostingRuleTemplate,
): PostingRuleFormState => ({
  ...createPostingRuleFormFromEventDefinition({
    value: template.eventType,
    label: `${template.ruleNamePrefix} rule`,
    sourceModule: template.sourceModule,
    description: template.description,
    defaultMetadata: {
      category: 'refund',
      scenario: template.id,
      channel: template.sourceModule,
    },
  }),
  ruleName: `${template.ruleNamePrefix} rule`,
});

export const getRefundTemplateRuleCount = (
  rules: FinancialPostingRuleRecord[],
  template: RefundPostingRuleTemplate,
): number =>
  rules.filter(
    (rule) =>
      rule.source_module === template.sourceModule &&
      rule.event_type === template.eventType,
  ).length;
